"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.makeSceneModel = exports.SceneModel = exports.SceneModelState = void 0;
const ValueObjects_1 = require("../ValueObjects");
const MemoizedNumber_1 = require("./MemoizedNumber");
const MemoizedQuaternion_1 = require("./MemoizedQuaternion");
const MemoizedString_1 = require("./MemoizedString");
const MemoizedVector3_1 = require("./MemoizedVector3");
const ObservableEntity_1 = require("./ObservableEntity");
const SceneModelObject_1 = require("./SceneModelObject");
var SceneModelState;
(function (SceneModelState) {
    SceneModelState["INIT"] = "Initialized";
    SceneModelState["LOADING"] = "Loading";
    SceneModelState["READY"] = "Ready";
    SceneModelState["ERROR"] = "Error";
})(SceneModelState = exports.SceneModelState || (exports.SceneModelState = {}));
class SceneModel extends ObservableEntity_1.ObservableEntity {
}
exports.SceneModel = SceneModel;
function makeSceneModel(id) {
    return new SceneModelImp(id);
}
exports.makeSceneModel = makeSceneModel;
class SceneModelImp extends SceneModel {
    constructor(id) {
        super();
        this._name = new MemoizedString_1.MemoizedString("", this.notify);
        this._boundsMin = new MemoizedVector3_1.MemoizedVector3(ValueObjects_1.Vector3.Zero(), this.notify);
        this._boundsMax = new MemoizedVector3_1.MemoizedVector3(ValueObjects_1.Vector3.Zero(), this.notify);
        this._defaultPosition = new MemoizedVector3_1.MemoizedVector3(ValueObjects_1.Vector3.Zero(), this.notify);
        this._defaultRotation = new MemoizedQuaternion_1.MemoizedQuaternion(ValueObjects_1.Quaternion.Identity(), this.notify);
        this._defaultScale = new MemoizedNumber_1.MemoizedNumber(1, this.notify);
        this._state = new MemoizedString_1.MemoizedString(SceneModelState.INIT, this.notify);
        this.sceneModelObjectLookup = new Map();
        this.hasObject = (id) => {
            return this.sceneModelObjectLookup.has(id);
        };
        this.getObjectById = (id) => {
            return this.sceneModelObjectLookup.get(id);
        };
        this.getObjectByPath = (path) => {
            let rVal;
            this.sceneModelObjectLookup.forEach((sceneModelObject) => {
                if (sceneModelObject.hasMeshPath(path)) {
                    rVal = sceneModelObject;
                }
            });
            return rVal;
        };
        this.addObject = (object) => {
            if (this.sceneModelObjectLookup.has(object.id)) {
                const orig = this.sceneModelObjectLookup.get(object.id);
                orig === null || orig === void 0 ? void 0 : orig.removeObserver(this.notify);
            }
            this.sceneModelObjectLookup.set(object.id, object);
            object.addObserver(this.notify);
            this.notify();
        };
        this.removeObject = (id) => {
            const orig = this.sceneModelObjectLookup.get(id);
            if (orig) {
                this.sceneModelObjectLookup.delete(id);
                orig.removeObserver(this.notify);
                this.notify();
            }
        };
        this.getAllObjects = () => {
            return Array.from(this.sceneModelObjectLookup.values());
        };
        this.applyData = (data) => {
            if (data.version === 0) {
                this.applyDataV0(data);
            }
            else if (data.version === 1) {
                this.applyDataV1(data);
            }
            else {
                throw new Error(`applyData unsupported version number: ${data.version}`);
            }
        };
        this.toData = () => {
            const data = {
                version: 1,
                bounds_max: this.bounds_max.dto,
                bounds_min: this.bounds_min.dto,
                defaultPosition: this.defaultPosition.dto,
                defaultRotation: this.defaultRotation.dto,
                defaultScale: this.defaultScale,
                name: this.name,
                objects: this.getAllObjects().map((obj) => obj.toData()),
            };
            return data;
        };
        this.id = id;
    }
    get name() {
        return this._name.val;
    }
    set name(name) {
        this._name.val = name;
    }
    get bounds_min() {
        return this._boundsMin.val;
    }
    set bounds_min(bounds_min) {
        this._boundsMin.val = bounds_min;
    }
    get bounds_max() {
        return this._boundsMax.val;
    }
    set bounds_max(bounds_max) {
        this._boundsMax.val = bounds_max;
    }
    get defaultPosition() {
        return this._defaultPosition.val;
    }
    set defaultPosition(position) {
        this._defaultPosition.val = position;
    }
    get defaultRotation() {
        return this._defaultRotation.val;
    }
    set defaultRotation(rotation) {
        this._defaultRotation.val = rotation;
    }
    get defaultScale() {
        return this._defaultScale.val;
    }
    set defaultScale(scale) {
        this._defaultScale.val = scale;
    }
    get state() {
        return this._state.val;
    }
    set state(val) {
        this._state.val = val;
    }
    get error() {
        return this._error;
    }
    set error(val) {
        this._error = val;
        this._state.val = SceneModelState.ERROR;
    }
    applyDataV0(data) {
        this._name.setValQuietly(data.name);
        this._boundsMin.setValQuietly(ValueObjects_1.Vector3.FromDTO(data.bounds_min));
        this._boundsMax.setValQuietly(ValueObjects_1.Vector3.FromDTO(data.bounds_max));
        this._defaultPosition.setValQuietly(ValueObjects_1.Vector3.FromDTO(data.defaultPosition));
        this._defaultScale.setValQuietly(data.defaultScale);
        this._defaultRotation.setValQuietly(ValueObjects_1.Quaternion.FromDTO(data.defaultRotation));
        const objectsToKeep = [];
        data.objects.forEach((objectDTO) => {
            const existingObj = this.sceneModelObjectLookup.get(objectDTO.id);
            if (existingObj) {
                existingObj.applyData(objectDTO);
            }
            else {
                const newObj = SceneModelObject_1.makeSceneModelObject(objectDTO);
                this.addObject(newObj);
            }
            objectsToKeep.push(objectDTO.id);
        });
        const allObjects = Array.from(this.sceneModelObjectLookup.keys());
        allObjects.forEach((obj) => {
            if (!objectsToKeep.includes(obj)) {
                this.removeObject(obj);
            }
        });
        this.notify();
    }
    applyDataV1(data) {
        this._name.setValQuietly(data.name);
        this._boundsMin.setValQuietly(ValueObjects_1.Vector3.FromDTO(data.bounds_min));
        this._boundsMax.setValQuietly(ValueObjects_1.Vector3.FromDTO(data.bounds_max));
        this._defaultPosition.setValQuietly(ValueObjects_1.Vector3.FromDTO(data.defaultPosition));
        this._defaultScale.setValQuietly(data.defaultScale);
        this._defaultRotation.setValQuietly(ValueObjects_1.Quaternion.FromDTO(data.defaultRotation));
        const objectsToKeep = [];
        data.objects.forEach((objectDTO) => {
            const existingObj = this.sceneModelObjectLookup.get(objectDTO.id);
            if (existingObj) {
                existingObj.applyData(objectDTO);
            }
            else {
                const newObj = SceneModelObject_1.makeSceneModelObject(objectDTO);
                this.addObject(newObj);
            }
            objectsToKeep.push(objectDTO.id);
        });
        const allObjects = Array.from(this.sceneModelObjectLookup.keys());
        allObjects.forEach((obj) => {
            if (!objectsToKeep.includes(obj)) {
                this.removeObject(obj);
            }
        });
        this.notify();
    }
}
